/**
 * The layers the shell's settings list edits on the public door (I-2510),
 * as stores of `ConnectSettingsScope`:
 *
 * - the session's own layer — its active profile, the helper's default
 *   target: the shared store of each key (`useAccountConfig`), so a change
 *   shows in the shell at once;
 * - another layer of the session's account (the default profile while the
 *   session works in another one): the same keys, written with `?target=`;
 * - an appearance the session may edit: read as a preview of that appearance
 *   (`?appearance=<id>&layers=1`), written with `target=appearance:<id>`.
 *
 * A change is one field, written as a merge patch of the layer; until master
 * answered, the store shows it over what it read.
 */
import { ref, shallowReactive, type Ref } from 'vue';
import {
  ACCOUNT_CONFIG_ERROR,
  accountConfigLayerView,
  appearanceLayerTarget,
  connectSettingsFieldPatch,
  fetchAccountConfigs,
  formatAccountConfigTarget,
  loadAccountConfigs,
  useAccountConfig,
  withPendingLayerPatches,
  type AccountConfigError,
  type AccountConfigHandle,
  type AccountConfigLayerRef,
  type AccountConfigLayerView,
  type AccountConfigResult,
  type ConnectSettingsScopeStore,
  type JsonObject,
} from '@blpde/connect';

type Translate = (key: string, named?: Record<string, unknown>) => string;

/** A failed read or write in words. */
export function describeSettingsError(t: Translate, error: AccountConfigError | null): string {
  if (error?.code === ACCOUNT_CONFIG_ERROR.fieldEnforced) {
    return error.field
      ? t('settingsList.errors.lockedField', { field: error.field })
      : t('settingsList.errors.locked');
  }
  if (error?.status === 403) return t('settingsList.errors.notAllowed');
  return t('settingsList.errors.save');
}

/** The changes on their way to a layer, per key (shown over what was read). */
function overlays() {
  const pending = shallowReactive(new Map<string, readonly JsonObject[]>());
  return {
    of: (key: string): readonly JsonObject[] => pending.get(key) ?? [],
    add(key: string, patch: JsonObject): void {
      pending.set(key, [...(pending.get(key) ?? []), patch]);
    },
    drop(key: string, patch: JsonObject): void {
      pending.set(
        key,
        (pending.get(key) ?? []).filter((item) => item !== patch)
      );
    },
  };
}

export interface SessionLayerStoreOptions {
  /** The layer to edit; default: the session's own (its active profile). */
  target?: AccountConfigLayerRef;
  /** After a write was answered (a new appearance makes every key read again). */
  afterWrite?: (key: string) => Promise<void> | void;
  t: Translate;
}

/**
 * A layer of the session's own account: the session's layer (default) or,
 * with `target`, another one it may write (`account:<id>`, the default
 * profile while the session works in another).
 */
export function createSessionLayerStore(options: SessionLayerStoreOptions): ConnectSettingsScopeStore {
  const handles = shallowReactive(new Map<string, AccountConfigHandle<JsonObject>>());
  const pending = overlays();
  const loading = ref(false);
  const error: Ref<string | null> = ref(null);

  function handleOf(key: string): AccountConfigHandle<JsonObject> {
    let handle = handles.get(key);
    if (!handle) {
      handle = useAccountConfig<JsonObject>(key, { layers: true, immediate: false });
      handles.set(key, handle);
    }
    return handle;
  }

  function view(key: string): AccountConfigLayerView | null {
    const handle = handles.get(key);
    const layers = handle?.layers.value;
    const target = options.target ?? handle?.target.value;
    if (!handle || !layers || !target) return null;
    return withPendingLayerPatches(accountConfigLayerView(layers, target), pending.of(key));
  }

  async function load(keys: readonly string[]): Promise<void> {
    const missing = keys.filter((key) => {
      const handle = handleOf(key);
      return !handle.loaded.value || !handle.layers.value;
    });
    if (!missing.length) return;
    loading.value = true;
    try {
      await loadAccountConfigs(missing);
      if (missing.some((key) => handleOf(key).error.value && !handleOf(key).layers.value)) {
        error.value = options.t('settingsList.errors.load');
      }
    } finally {
      loading.value = false;
    }
  }

  function set(key: string, path: readonly string[], value: unknown): void {
    const handle = handleOf(key);
    const patch = connectSettingsFieldPatch(view(key)?.own ?? {}, path, value);
    error.value = null;
    if (!options.target) {
      // The session's own layer: the shared store shows the change at once.
      void handle.patch(patch).then(async (result) => {
        if (!result) error.value = describeSettingsError(options.t, handle.error.value);
        else await options.afterWrite?.(key);
      });
      return;
    }
    pending.add(key, patch);
    void handle
      .patch(patch, { target: formatAccountConfigTarget(options.target) })
      .then(async (result) => {
        if (!result) error.value = describeSettingsError(options.t, handle.error.value);
        else await handle.reload();
        pending.drop(key, patch);
        if (result) await options.afterWrite?.(key);
      });
  }

  return { load, view, set, loading, error };
}

/**
 * An appearance the session may edit: its layer of every key over what
 * platform, tenant and company give, as the preview of that appearance
 * answers it (`?appearance=<id>&layers=1`).
 */
export function createAppearanceLayerStore(id: string, t: Translate): ConnectSettingsScopeStore {
  const results = shallowReactive(new Map<string, AccountConfigResult<JsonObject>>());
  const pending = overlays();
  const loading = ref(false);
  const error: Ref<string | null> = ref(null);
  const target = appearanceLayerTarget(id);

  async function read(keys: readonly string[]): Promise<void> {
    const batch = await fetchAccountConfigs<JsonObject>(keys, { appearance: id, layers: true });
    for (const [key, result] of Object.entries(batch.configs)) results.set(key, result);
    if (Object.keys(batch.errors).length) error.value = t('settingsList.errors.load');
  }

  function view(key: string): AccountConfigLayerView | null {
    const layers = results.get(key)?.layers;
    if (!layers) return null;
    return withPendingLayerPatches(accountConfigLayerView(layers, target), pending.of(key));
  }

  async function load(keys: readonly string[]): Promise<void> {
    const missing = keys.filter((key) => !results.has(key));
    if (!missing.length) return;
    loading.value = true;
    try {
      await read(missing);
    } catch {
      error.value = t('settingsList.errors.load');
    } finally {
      loading.value = false;
    }
  }

  function set(key: string, path: readonly string[], value: unknown): void {
    const patch = connectSettingsFieldPatch(view(key)?.own ?? {}, path, value);
    const handle = useAccountConfig<JsonObject>(key, { immediate: false });
    error.value = null;
    pending.add(key, patch);
    void handle.patch(patch, { target: `appearance:${id}` }).then(async (result) => {
      if (!result) error.value = describeSettingsError(t, handle.error.value);
      try {
        await read([key]);
      } catch {
        /* the next read shows it */
      }
      pending.drop(key, patch);
    });
  }

  return { load, view, set, loading, error };
}
